/**
 * The document and slides editors' controller hook (plan 7.3), shared by the
 * web Tiptap editor and the native block editor: loads the head document
 * (version 1 text escaped once, so the editor always works in version 2),
 * saves the whole document 800 ms after the last change (`PUT document`,
 * `schemaVersion: 2`), holds a document over a limit with the limit's words,
 * flushes on Done, close and app background, reloads on Reload after a
 * conflict, and registers the editor for the suggestions card.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArtifactDocument } from '@tabbio/types';
import { getArtifact } from '@/lib/artifacts-api';
import { saveArtifactDocument } from '@/lib/artifact-edits-api';
import { useInvalidateArtifact } from '@/hooks/useArtifactVersions';
import { noteDocumentSave } from '../suggestions/suggestions-store';
import { registerActiveEditor } from './active-editor';
import { documentForSave, documentLimitIssue, limitCopyKey, toEditableDocument } from './document-editor-model';
import { DocumentSaveQueue } from './document-save-queue';
import { createUuid } from './editor-ids';
import type { EditorSaveState } from './save-state';

export type DocumentEditorLoad =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; versionId: string; generation: number };

export type DocumentEditorController = {
  load: DocumentEditorLoad;
  /** The document being edited (version 2 markup). */
  document: ArtifactDocument | null;
  /**
   * Grows when the document changed from outside the editor (a reload, an
   * applied suggestion), so a rich editor sets its content again.
   */
  externalRevision: number;
  saveState: EditorSaveState;
  /** A change from the editor. */
  update: (document: ArtifactDocument) => void;
  flush: () => Promise<boolean>;
  retry: () => void;
  reload: () => void;
  baseVersionId: () => string | null;
};

export function useDocumentEditorController(options: {
  artifactId: string;
  /** The limit's words in the reader's language. */
  translate: (key: string, params?: Record<string, unknown>) => string;
  onBackground?: (flush: () => void) => () => void;
}): DocumentEditorController {
  const { artifactId } = options;
  const invalidate = useInvalidateArtifact();
  const [load, setLoad] = useState<DocumentEditorLoad>({ status: 'loading' });
  const [document, setDocument] = useState<ArtifactDocument | null>(null);
  const [externalRevision, setExternalRevision] = useState(0);
  const [saveState, setSaveState] = useState<EditorSaveState>({ status: 'idle' });
  const editSessionId = useMemo(() => createUuid(), []);
  const queueRef = useRef<DocumentSaveQueue | null>(null);
  const documentRef = useRef<ArtifactDocument | null>(null);
  const translateRef = useRef(options.translate);
  translateRef.current = options.translate;
  const generation = useRef(0);
  const alive = useRef(true);

  const fetchDocument = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      const detail = await getArtifact(artifactId);
      const versionId = detail.currentVersion?.id;
      if (!alive.current) return;
      if (!detail.document || !versionId) {
        setLoad({ status: 'error' });
        return;
      }
      const editable = toEditableDocument(detail.document);
      generation.current += 1;
      queueRef.current?.reset(versionId);
      documentRef.current = editable;
      setDocument(editable);
      setExternalRevision((value) => value + 1);
      setLoad({ status: 'ready', versionId, generation: generation.current });
    } catch {
      if (alive.current) setLoad({ status: 'error' });
    }
  }, [artifactId]);

  useEffect(() => {
    alive.current = true;
    const queue = new DocumentSaveQueue('', editSessionId, {
      save: (request) => saveArtifactDocument(artifactId, request),
      validate: (candidate) => {
        const issue = documentLimitIssue(candidate);
        return issue ? translateRef.current(limitCopyKey(issue), { max: issue.max }) : null;
      },
      onSaved: (result, _document, baseVersionId) => {
        // Suggestions made on the base follow the document.
        noteDocumentSave(artifactId, baseVersionId, result.version.id);
        if (!result.squashed) invalidate(artifactId);
      },
      onStateChange: (state) => {
        if (alive.current) setSaveState(state);
      },
    });
    queueRef.current = queue;
    void fetchDocument();
    return () => {
      alive.current = false;
      queueRef.current = null;
      void queue.flush().finally(() => {
        queue.dispose();
        invalidate(artifactId);
      });
    };
    // One editing session per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId]);

  const onBackground = options.onBackground;
  useEffect(() => {
    if (!onBackground) return;
    return onBackground(() => {
      void queueRef.current?.flush();
    });
  }, [onBackground]);

  const update = useCallback((next: ArtifactDocument) => {
    documentRef.current = next;
    setDocument(next);
    queueRef.current?.update(documentForSave(next));
  }, []);

  useEffect(() => {
    if (load.status !== 'ready') return;
    const versionId = load.versionId;
    return registerActiveEditor({
      artifactId,
      kind: 'document',
      baseVersionId: () => queueRef.current?.baseVersionId || versionId,
      document: () => documentRef.current,
      applyDocument: (change) => {
        const current = documentRef.current;
        if (!current) return;
        // The save carrying a suggestion is counted as `suggest-apply`.
        queueRef.current?.markNextSave('suggestion');
        update(change(current));
        setExternalRevision((value) => value + 1);
      },
    });
  }, [artifactId, load, update]);

  const flush = useCallback(() => queueRef.current?.flush() ?? Promise.resolve(true), []);
  const retry = useCallback(() => {
    void queueRef.current?.retry();
  }, []);
  const reload = useCallback(() => {
    void fetchDocument();
  }, [fetchDocument]);
  const baseVersionId = useCallback(
    () => queueRef.current?.baseVersionId || (load.status === 'ready' ? load.versionId : null),
    [load],
  );

  return { load, document, externalRevision, saveState, update, flush, retry, reload, baseVersionId };
}
